/**
 * @fileoverview Membership total-count trend chart with an always-visible Added/Cancelled
 * per-year breakdown on the x-axis.
 * @description Ported from the InsightBox Banking design system's own
 * `charts/MemberMovementChart.tsx` — same colored-by-direction gradient/dot treatment as
 * `TrendArea`, plus a custom x-axis tick showing the signed Added/Cancelled pair under each year so
 * that movement doesn't require a hover, and a custom tooltip with a Total/Added/Cancelled/Net
 * breakdown. Passed as function props (`tick={XAxisTick}`, `content={MemberTooltip}`) rather than
 * the reference's `tick={<XAxisTick />}` element form, so recharts' own exported prop types
 * (`XAxisTickContentProps`/`TooltipContentProps`) apply directly instead of needing an untyped
 * `any` — both forms render identically at runtime. Feature-scoped rather than promoted to
 * `shared/` since `MemberPoint` is banking-specific.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  CHART_ACCENT,
  CHART_ERROR,
  CHART_GRID,
  CHART_SUCCESS,
  CHART_WARNING,
} from '@/shared/constants/chart-theme.constants'

import type {
  DotItemDotProps,
  LabelProps,
  TooltipContentProps,
  XAxisTickContentProps,
} from 'recharts'

export interface MemberPoint {
  fy: string
  total: number
  added: number
  cancelled: number
}

/** Rise into a point = green, fall = red, no change at all = amber. The first point has no prior
 * value of its own, so it borrows the direction of the segment leaving it. */
function pointColor(data: MemberPoint[], index: number): string {
  if (index === 0) {
    if (data.length > 1 && data[1]!.total === data[0]!.total) return CHART_WARNING
    return data.length > 1 && data[1]!.total < data[0]!.total ? CHART_ERROR : CHART_SUCCESS
  }
  if (data[index]!.total === data[index - 1]!.total) return CHART_WARNING
  return data[index]!.total >= data[index - 1]!.total ? CHART_SUCCESS : CHART_ERROR
}

function growthPct(data: MemberPoint[], index: number): number | null {
  if (index === 0) return null
  const prev = data[index - 1]!.total
  if (prev === 0) return null
  return ((data[index]!.total - prev) / prev) * 100
}

function makeTotalDot(data: MemberPoint[]) {
  return function TotalDot(props: DotItemDotProps) {
    const { cx, cy, index } = props
    if (typeof cx !== 'number' || typeof cy !== 'number') return null
    return (
      <circle cx={cx} cy={cy} r={5} fill={pointColor(data, index)} stroke="#fff" strokeWidth={2} />
    )
  }
}

/** Value on top, YoY growth% right below — same two-line label style as the Business Performance
 * trend charts (Total Business / Net Profit). */
function makeTotalLabel(data: MemberPoint[]) {
  return function TotalLabel(props: LabelProps) {
    const { x, y, index } = props
    const point = index === undefined ? undefined : data[index]
    if (!point || typeof x !== 'number' || typeof y !== 'number') return null
    const growth = growthPct(data, index!)
    return (
      <g>
        <text x={x} y={y - 34} textAnchor="middle" className="fill-accent text-[15px] font-bold">
          {point.total.toLocaleString('en-IN')}
        </text>
        {growth !== null && (
          <text
            x={x}
            y={y - 18}
            textAnchor="middle"
            className="text-[11px] font-medium"
            fill={growth >= 0 ? CHART_SUCCESS : CHART_ERROR}
          >
            {growth >= 0 ? '+' : ''}
            {growth.toFixed(2)}%
          </text>
        )}
      </g>
    )
  }
}

/** Smooth gradient: one stop per point (green if it rose, red if it fell), fading through amber at
 * each direction change — same treatment as the Business Performance trend charts. */
function buildSegmentStops(data: MemberPoint[]): { offset: number; color: string }[] {
  const stops: { offset: number; color: string }[] = []
  data.forEach((_, i) => {
    const offset = (i / (data.length - 1)) * 100
    stops.push({ offset, color: pointColor(data, i) })
    if (i < data.length - 1) {
      const nextOffset = ((i + 1) / (data.length - 1)) * 100
      if (pointColor(data, i) !== pointColor(data, i + 1)) {
        stops.push({ offset: (offset + nextOffset) / 2, color: CHART_WARNING })
      }
    }
  })
  return stops
}

function roundNice(value: number, max: number): string {
  if (max >= 100) return Math.round(value).toLocaleString('en-IN')
  if (max >= 10) return value.toFixed(1)
  return value.toFixed(2)
}

/** FY label on top, then a signed Added/Cancelled pair right below it — always-visible per-year
 * movement so the count doesn't require a hover. */
function makeXAxisTick(data: MemberPoint[]) {
  return function XAxisTick(props: XAxisTickContentProps) {
    const { x, y, payload } = props
    const point = data.find((d) => d.fy === payload.value)
    if (!point || typeof x !== 'number' || typeof y !== 'number') return null
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={13} textAnchor="middle" fill="#111827" fontSize={13} fontWeight={600}>
          {payload.value}
        </text>
        <text
          x={0}
          y={0}
          dy={30}
          textAnchor="middle"
          fill={CHART_SUCCESS}
          fontSize={11}
          fontWeight={700}
        >
          +{point.added.toLocaleString('en-IN')}
        </text>
        <text
          x={0}
          y={0}
          dy={44}
          textAnchor="middle"
          fill={CHART_ERROR}
          fontSize={11}
          fontWeight={700}
        >
          -{point.cancelled.toLocaleString('en-IN')}
        </text>
      </g>
    )
  }
}

/** Custom tooltip — the only place Added/Cancelled numbers show up, as a signed +/- pair, so the
 * chart itself stays to a single trend line. */
function MemberTooltip({ active, payload }: TooltipContentProps) {
  if (!active || !payload?.length) return null
  // recharts types a tooltip payload entry's own `payload` (the underlying data point) as `any` in
  // its own public API — there's no narrower type to reach for here, so this one assertion is the
  // actual "the payload for this chart's tooltip entry is a MemberPoint" contract, not a shortcut
  // around a real type.
  const point = payload[0]!.payload as MemberPoint
  const net = point.added - point.cancelled
  return (
    <div className="rounded-xl border border-border bg-white px-3.5 py-2.5 shadow-card">
      <p className="text-xs font-semibold text-label">{point.fy}</p>
      <p className="mt-1 text-sm font-bold text-accent">
        {point.total.toLocaleString('en-IN')} Total Members
      </p>
      <div className="mt-1.5 space-y-0.5 border-t border-border pt-1.5 text-xs">
        <p className="flex items-center justify-between gap-4">
          <span className="text-label">New Members</span>
          <span className="font-semibold text-success">+{point.added.toLocaleString('en-IN')}</span>
        </p>
        <p className="flex items-center justify-between gap-4">
          <span className="text-label">Cancellations</span>
          <span className="font-semibold text-danger">
            -{point.cancelled.toLocaleString('en-IN')}
          </span>
        </p>
        <p className="flex items-center justify-between gap-4 font-semibold text-text">
          <span>Net Change</span>
          <span className={net >= 0 ? 'text-success' : 'text-danger'}>
            {net >= 0 ? '+' : ''}
            {net.toLocaleString('en-IN')}
          </span>
        </p>
      </div>
    </div>
  )
}

interface MemberMovementChartProps {
  data: MemberPoint[]
  height?: number | '100%'
}

/**
 * @component
 */
function MemberMovementChart({ data, height = 340 }: MemberMovementChartProps) {
  const totals = data.map((d) => d.total)
  const max = Math.max(...totals)
  const min = Math.min(...totals)

  const TotalDot = makeTotalDot(data)
  const TotalLabel = makeTotalLabel(data)
  const XAxisTick = makeXAxisTick(data)
  const segmentStops = buildSegmentStops(data)

  // Modest, mostly-fixed headroom rather than a large range-fraction pad — Membership's range is
  // small relative to its absolute values (a ~7% swing on a ~9,500 base), so a
  // Business-Performance-style fractional pad left a tall dead band above the curve. margin.top
  // (below) already reserves the pixel space the value+growth% label needs above the highest
  // point.
  const range = max - min || max * 0.1

  return (
    // In fill mode (height="100%"), absolutely fill the nearest positioned ancestor instead of
    // relying on a plain CSS percentage height — see TrendArea.tsx for why. No-op for a fixed
    // numeric height.
    <ResponsiveContainer
      width="100%"
      height={height}
      debounce={200}
      {...(height === '100%' ? { style: { position: 'absolute' as const, inset: 0 } } : {})}
    >
      <AreaChart data={data} margin={{ top: 44, right: 24, left: 8, bottom: 8 }}>
        <defs>
          <linearGradient id="memberTrendFill" x1="0" y1="0" x2="1" y2="0">
            {segmentStops.map((s, i) => (
              <stop key={i} offset={`${s.offset}%`} stopColor={s.color} stopOpacity={0.4} />
            ))}
          </linearGradient>
        </defs>
        <CartesianGrid stroke={CHART_GRID} vertical={false} />
        <XAxis dataKey="fy" axisLine={false} tickLine={false} tick={XAxisTick} height={54} />
        <YAxis
          domain={[Math.max(0, min - range * 0.15), max + range * 0.35]}
          tickFormatter={(v: number) => roundNice(v, max)}
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#6B7280', fontSize: 11 }}
          width={48}
        />
        <Tooltip content={MemberTooltip} />
        <Area
          type="monotone"
          dataKey="total"
          name="Total Members"
          stroke={CHART_ACCENT}
          strokeWidth={2.5}
          fill="url(#memberTrendFill)"
          dot={TotalDot}
          activeDot={{ r: 6 }}
          label={TotalLabel}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default MemberMovementChart
